import { MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { AuthProvider } from "./auth/AuthContext";
import { theme } from "./theme/mantine-theme";

/**
 * STYLESHEET ORDER IS LOAD-BEARING. Do not sort these imports.
 *
 *   1. Inter, self-hosted. Not from a CDN: the shop runs on slow and
 *      sometimes absent connections, and a font fetched from a third party is
 *      a render-blocking request to a host we do not control. @fontsource
 *      ships the files into our own bundle, so they are served from our
 *      origin - which is also the only thing the CSP's font-src allows.
 *   2. Mantine's own styles, which define the CSS variables everything else
 *      reads.
 *   3. Our global.css, which overrides a few of them and holds the handful of
 *      rules no theme token can express.
 *
 * There used to be a fourth: the legacy styles.css, loaded last so that
 * screens not yet migrated kept their layout. Every screen is migrated, so it
 * is gone - and it had stopped being merely redundant. Its
 * `input, textarea, select { min-height: var(--tap) }` applied to Mantine's
 * checkboxes too, stretching each one to 48px so the rows overlapped and a
 * click on one role landed on its neighbour.
 */
import "@fontsource/inter/latin-400.css";
import "@fontsource/inter/latin-500.css";
import "@fontsource/inter/latin-600.css";
import "@fontsource/inter/latin-700.css";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "./theme/global.css";

const container = document.getElementById("root");
if (!container) throw new Error("Root element missing from index.html");

createRoot(container).render(
  <StrictMode>
    {/*
      forceColorScheme="light" is deliberate and temporary.

      The business asked for light only "for now", and the theme carries no
      dark values - theme.other.state.* holds literal light hex pairs with no
      dark counterpart, so a dark scheme would render workflow badges that are
      unreadable rather than merely wrong. Forcing it is honest about that;
      offering a toggle that produced illegible badges would not be.

      When dark is wanted: give every theme.other.state entry a dark pair,
      then swap this for defaultColorScheme and add the toggle.
    */}
    <MantineProvider theme={theme} forceColorScheme="light">
      <ModalsProvider>
        <Notifications position="top-center" autoClose={4000} />
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </ModalsProvider>
    </MantineProvider>
  </StrictMode>,
);
